import React from 'react';
import { motion } from 'framer-motion';
import { Check, Shield, Zap, Users, Wallet, Lock, ArrowRight, Download, Star, UserCircle } from 'lucide-react';
import { CheckCircle } from 'lucide-react';

const benefits = [
  { icon: Lock, title: 'Digital Security', description: 'Every payment is recorded. Every request is tracked.', gradient: 'from-blue-500 to-cyan-500' },
  { icon: Wallet, title: 'Financial Inclusion', description: 'Build credit history through consistent digital payments.', gradient: 'from-emerald-500 to-green-500' },
  { icon: Zap, title: 'Rent Mdogo Mdogo', description: 'Pay KES 15,000/month instead of KES 40,000 lump sum rent.', gradient: 'from-purple-500 to-pink-500' },
  { icon: Shield, title: 'Peace of Mind', description: 'Insurance protects you from the unexpected.', gradient: 'from-orange-500 to-red-500' },
  { icon: Zap, title: 'Convenience', description: 'KPLC tokens, water bills, and rent in one place.', gradient: 'from-cyan-500 to-blue-500' },
  { icon: Users, title: 'Community Support', description: 'Connect with millions of renters like you.', gradient: 'from-pink-500 to-rose-500' },
];

const features = [
  'Instant payment receipts & records',
  'Automatic deposit protection',
  'Insurance at competitive rates',
  'Real-time maintenance tracking',
  'Access to 12M+ community',
  'Financial history & credit building',
  'M-Pesa & mobile money support',
  'Rent Mdogo Mdogo — pay rent in comfortable monthly bites',
];

const testimonial = {
  name: 'Anne',
  location: 'Nairobi',
  text: "I used to argue with my landlord about deposits every time. With YAWOTE, everything is documented with photos. I got my full deposit back for the first time in 5 years.",
};

export default function TenantsPage() {
  return (
    <div className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
            For Tenants
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Take Control of Your{' '}
            <span className="text-gradient">Rental Life.</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            YAWOTE empowers you with tools, community, and opportunities you never had before.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit, idx) => {
            const IconComponent = benefit.icon;
            return (
              <motion.div
                key={idx}
                className="group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
                <div className="relative card">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <IconComponent className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          className="rounded-2xl p-12 mb-20 relative overflow-hidden"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/20 rounded-2xl"></div>
          <div className="relative">
            <h2 className="text-3xl font-bold text-white mb-8">Everything You Get</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="text-green-400" size={18} />
                  </div>
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          className="card max-w-3xl mx-auto mb-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="flex gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <p className="text-gray-300 text-lg italic mb-6">"{testimonial.text}"</p>
          <div className="flex items-center gap-3 pt-4 border-t border-white/5">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center ring-2 ring-blue-500/20">
              <UserCircle className="text-white" size={24} />
            </div>
            <div>
              <p className="font-bold text-white">{testimonial.name}</p>
              <p className="text-gray-400 text-sm">{testimonial.location}</p>
            </div>
          </div>
        </motion.div>

        <motion.div className="text-center" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.8 }} viewport={{ once: true }}>
          <a href="https://play.google.com/store/apps/details?id=com.yawote.app" target="_blank" rel="noopener noreferrer">
            <button className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-4 group">
              <Download size={20} className="group-hover:scale-110 transition-transform" />
              Download YAWOTE for Tenants
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </a>
        </motion.div>
      </div>
    </div>
  );
}
